import useClient from 'api/client';
import addItemsAndSubmit from 'api/ShareObject/addItemsAndSubmit';
import approveShareObjectMutation from 'api/ShareObject/approveShareObjectMutation';
import removeSharedItem from 'api/ShareObject/removeSharedItem';
import submitApproval from 'api/ShareObject/submitApproval';
import { AddShareItemsInput, ShareItem } from 'types/Share';

interface AddItemsShareProps {
    addShareItemsInput: AddShareItemsInput[];
    removeShareItems: ShareItem[];
    shareUri: string;
    handleNext: (data?: {
        autoApproved?: boolean;
        creationError?: string;
    }) => { autoApproved?: boolean; creationError?: string } | void;
}

export default function useEditShareRequestItems(autoapprove: boolean = false) {
    const client = useClient();

    const editShareRequestItems = async ({
        addShareItemsInput,
        removeShareItems,
        shareUri,
        handleNext,
    }: AddItemsShareProps) => {
        const response = await client.mutate(
            addItemsAndSubmit(shareUri, addShareItemsInput),
        );

        let autoApproved: boolean = false;
        let creationError: string = '';
        // If the submission was successful and the user is an approver we will auto-approve the share object
        if (!response.errors) {
            if (autoapprove) {
                const approvalResponse = await client.mutate(
                    approveShareObjectMutation(shareUri),
                );

                // If the approval is successful we will redirect the user to the share object, else we will display the error
                if (approvalResponse.errors)
                    creationError =
                        'Autoaproval error, the item has been added and submitted but the autoaproval failed';
                else autoApproved = true;
                // If the user is not an approver we will just continue to the next step
            }
        }
        // If the adding items is not successful we will just display the issue
        else creationError = response.errors[0].message;

        // We will then also delete the ones that have been unselected
        const responses = await Promise.all(
            removeShareItems.map(async (shareItem: ShareItem): Promise<any> => {
                const response = await client.mutate(
                    removeSharedItem(shareItem.shareItemUri),
                );
                return response;
            }),
        );
        if (
            responses.some(
                (response) => response.errors && response.errors.length > 0,
            )
        ) {
            creationError = 'Error removing some share items';
        }
        await client.mutate(submitApproval(shareUri));
        handleNext({ creationError, autoApproved });
    };

    return editShareRequestItems;
}
